// src/hooks/klines/useSpreadKlines.js
import { useEffect, useMemo, useRef, useState } from "react";
import {
  gateSpotFetchHistory,    gateSpotOpenWS,
  gateFuturesFetchHistory, gateFuturesOpenWS,
  mexcSpotFetchHistory,    mexcSpotOpenWS,
  mexcFuturesFetchHistory, mexcFuturesOpenWS,
  bingxSpotFetchHistory,   bingxSpotOpenWS,
  bingxFuturesFetchHistory,bingxFuturesOpenWS,
  bitgetSpotFetchHistory,  bitgetSpotOpenWS,
  bitgetFuturesFetchHistory, bitgetFuturesOpenWS,
  bybitSpotFetchHistory,   bybitSpotOpenWS,
  bybitFuturesFetchHistory,bybitFuturesOpenWS,
  kucoinSpotFetchHistory,  kucoinSpotOpenWS,
  kucoinFuturesFetchHistory, kucoinFuturesOpenWS,
  binanceSpotFetchHistory, binanceSpotOpenWS,
  binanceFuturesFetchHistory, binanceFuturesOpenWS,
} from "./adapters";

import { lbankFuturesFetchHistory } from "./adapters/lbankFutures";
import { lbankSpotFetchHistory, lbankSpotOpenWS } from "./adapters/lbankSpot";
import { ourbitSpotFetchHistory } from "./adapters/ourbitSpot";
import { ourbitFuturesFetchHistory } from "./adapters/ourbitFutures";

// ============== ЛОГИ ============
const L = (...a) => console.log("[spread]", ...a);

// ============== ВСПОМОГАТЕЛЬНОЕ ============

// всё приводим к миллисекундам
function toMs(t) {
  const n = Number(t);
  if (!Number.isFinite(n)) return NaN;
  return n > 1e12 ? n : n * 1000; // авто-детект: сек → мс
}

function intervalToMs(interval) {
  switch (interval) {
    case "1m":  return 60_000;
    case "3m":  return 3 * 60_000;
    case "5m":  return 5 * 60_000;
    case "15m": return 15 * 60_000;
    case "30m": return 30 * 60_000;
    case "1h":  return 60 * 60_000;
    case "2h":  return 2 * 60 * 60_000;
    case "4h":  return 4 * 60 * 60_000;
    case "8h":  return 8 * 60 * 60_000;
    case "12h": return 12 * 60 * 60_000;
    case "1d":  return 24 * 60 * 60_000;
    default:    return 60_000;
  }
}

// некоторые биржи присылают конец свечи, некоторые начало —
// всегда щёлкаем на "начало корзины" для ИНТЕРСЕКЦИИ
function snapTimeToBucket(tAny, intervalMs) {
  const t = toMs(tAny);
  if (!Number.isFinite(t)) return NaN;
  return Math.floor(t / intervalMs) * intervalMs;
}

const toLwc = (t, o, h, l, c) => ({ time: t, open: o, high: h, low: l, close: c });

// нормализация символа под биржу
function fmtSymbol(exchange, type, base) {
  if (exchange === "gate")   return `${base}_USDT`;
  if (exchange === "mexc")   return type === "spot" ? `${base}USDT` : `${base}_USDT`;
  if (exchange === "bingx")  return `${base}-USDT`;
  if (exchange === "bitget") return type === "spot" ? `${base}USDT_SPBL` : `${base}USDT_UMCBL`;
  if (exchange === "bybit")  return `${base}USDT`; // и spot, и futures одинаково
  if (exchange === "kucoin" && type === "futures") {
    const b = base === "BTC" ? "XBT" : base; // BTC → XBT для фьючей KuCoin
    return `${b}USDTM`;
  }
  if (exchange === "kucoin" && type === "spot") {
    return `${base}-USDT`; // KuCoin spot с дефисом
  }
  if (exchange === "lbank" && type === "spot") {
    return `${String(base).toLowerCase()}_usdt`; // LBank spot — нижний регистр + подчёркивание
  }
  if (exchange === "lbank" && type === "futures") {
    return `${base}USDT`; // LBank futures instrumentID без разделителей
  }
  if (exchange === "binance") {
    return `${base}USDT`; // Binance spot/futures — без подчёркивания
  }
  if (exchange === "ourbit") return `${base}_USDT`;
  return `${base}_USDT`;
}

// ============== СПРЕД ============

function upsert(map, c, intervalMs) {
  if (!c) return;
  const o = Number(c.open), h = Number(c.high), l = Number(c.low), cl = Number(c.close);
  const bucket = snapTimeToBucket(c.time, intervalMs);
  if (![o, h, l, cl, bucket].every(Number.isFinite)) return;

  const prev = map.get(bucket);
  const merged = prev
    ? { ...prev, open: o, high: h, low: l, close: cl }
    : { time: bucket, open: o, high: h, low: l, close: cl };
  map.set(bucket, merged);
}

function buildSpreadFromMaps(aMap, bMap, limit = 300) {
  const ts = [];
  for (const t of aMap.keys()) if (bMap.has(t)) ts.push(t);
  ts.sort((x, y) => x - y);

  const out = [];
  for (const t of ts.slice(-limit)) {
    const left  = aMap.get(t);
    const right = bMap.get(t);
    if (!left || !right) continue;

    // (RIGHT / LEFT − 1) * 100
    const open  = (right.open  / left.open  - 1) * 100;
    const close = (right.close / left.close - 1) * 100;
    const high  = (right.high  / left.low   - 1) * 100; // худший сценарий
    const low   = (right.low   / left.high  - 1) * 100;

    out.push(toLwc(t, open, high, low, close));
  }
  return out;
}

// диагностика пересечения
function debugIntersection(leftMap, rightMap, tag) {
  const lKeys = Array.from(leftMap.keys()).sort((a,b)=>a-b);
  const rKeys = Array.from(rightMap.keys()).sort((a,b)=>a-b);

  const lMin = lKeys[0], lMax = lKeys[lKeys.length-1];
  const rMin = rKeys[0], rMax = rKeys[rKeys.length-1];

  const inter = [];
  const rSet = new Set(rKeys);
  for (const t of lKeys) if (rSet.has(t)) inter.push(t);

  L("intersect", tag, {
    leftSize: leftMap.size, rightSize: rightMap.size, interSize: inter.length,
    leftRange: { from: lMin, to: lMax },
    rightRange:{ from: rMin, to: rMax },
  });

  if (inter.length === 0) {
    const lSamples = lKeys.slice(-5);
    const rSamples = rKeys.slice(-5);
    L("no-intersection samples", { left: lSamples, right: rSamples });
  }
}

// ============== ХУК ============

function _useSpreadKlinesImpl({
  symbol,                    // "ETH"
  left,                      // { exchange: "...", type: "spot"|"futures" }
  right,                     // { exchange: "...", type: "spot"|"futures" }
  interval = "1m",
  limit = 300,
}) {
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const leftMapRef  = useRef(new Map());
  const rightMapRef = useRef(new Map());
  const rafRef      = useRef(null);

  const leftCloseWSRef  = useRef(null);
  const rightCloseWSRef = useRef(null);

  const key = useMemo(
    () => JSON.stringify({ symbol, left, right, interval, limit }),
    [symbol, left, right, interval, limit]
  );

  const recalc = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const out = buildSpreadFromMaps(leftMapRef.current, rightMapRef.current, limit);
      setCandles(out);
      L("setData FULL", {
        count: out.length,
        firstCandle: out[0],
        lastCandle: out[out.length - 1],
      });
    });
  };

  useEffect(() => {
    leftMapRef.current  = new Map();
    rightMapRef.current = new Map();
    setCandles([]);
    setLoading(true);
    setErr(null);

    try { leftCloseWSRef.current?.(); } catch {}
    try { rightCloseWSRef.current?.(); } catch {}
    leftCloseWSRef.current  = null;
    rightCloseWSRef.current = null;

    let cancelled = false;
    const intervalMs = intervalToMs(interval);

    const inputsOk = left?.exchange && left?.type && right?.exchange && right?.type && symbol;
    if (!inputsOk) {
      setLoading(false);
      setErr("Некорректные параметры useSpreadKlines");
      return;
    }

    const leftSym  = fmtSymbol(left.exchange,  left.type,  symbol);
    const rightSym = fmtSymbol(right.exchange, right.type, symbol);
    L("inputs", { symbol, interval, left, right, leftSym, rightSym, intervalMs });

    // ------- REST история
    const leftRest = (async () => {
      let arr = [];
      try {
        if (left.exchange === "gate" && left.type === "spot") {
          arr = await gateSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "gate" && left.type === "futures") {
          arr = await gateFuturesFetchHistory({ symbol: leftSym, interval, limit, settle: "usdt" });
        } else if (left.exchange === "mexc" && left.type === "spot") {
          arr = await mexcSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "mexc" && left.type === "futures") {
          arr = await mexcFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "bingx" && left.type === "spot") {
          arr = await bingxSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "bingx" && left.type === "futures") {
          arr = await bingxFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "bitget" && left.type === "spot") {
          arr = await bitgetSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "bitget" && left.type === "futures") {
          arr = await bitgetFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "bybit" && left.type === "spot") {
          arr = await bybitSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "bybit" && left.type === "futures") {
          arr = await bybitFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "kucoin" && left.type === "spot") {
          arr = await kucoinSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "kucoin" && left.type === "futures") {
          arr = await kucoinFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "lbank" && left.type === "spot") {
          arr = await lbankSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "lbank" && left.type === "futures") {
          arr = await lbankFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "binance" && left.type === "spot") {
          arr = await binanceSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "binance" && left.type === "futures") {
          arr = await binanceFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "ourbit" && left.type === "spot") {
          arr = await ourbitSpotFetchHistory({ symbol: leftSym, interval, limit });
        } else if (left.exchange === "ourbit" && left.type === "futures") {
          arr = await ourbitFuturesFetchHistory({ symbol: leftSym, interval, limit });
        } else {
          throw new Error("Left side not supported");
        }
      } catch (e) {
        console.error("[left REST] error", e);
      }
      if (cancelled) return;

      for (const c of arr) upsert(leftMapRef.current, c, intervalMs, "L");
      L("left REST normalized", {
        size: leftMapRef.current.size,
        first5: Array.from(leftMapRef.current.values()).slice(0,5),
        last5:  Array.from(leftMapRef.current.values()).slice(-5),
      });
    })();

    const rightRest = (async () => {
      let arr = [];
      try {
        if (right.exchange === "gate" && right.type === "spot") {
          arr = await gateSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "gate" && right.type === "futures") {
          arr = await gateFuturesFetchHistory({ symbol: rightSym, interval, limit, settle: "usdt" });
        } else if (right.exchange === "mexc" && right.type === "spot") {
          arr = await mexcSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "mexc" && right.type === "futures") {
          arr = await mexcFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "bingx" && right.type === "spot") {
          arr = await bingxSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "bingx" && right.type === "futures") {
          arr = await bingxFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "bitget" && right.type === "spot") {
          arr = await bitgetSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "bitget" && right.type === "futures") {
          arr = await bitgetFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "bybit" && right.type === "spot") {
          arr = await bybitSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "bybit" && right.type === "futures") {
          arr = await bybitFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "kucoin" && right.type === "spot") {
          arr = await kucoinSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "kucoin" && right.type === "futures") {
          arr = await kucoinFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "lbank" && right.type === "spot") {
          arr = await lbankSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "lbank" && right.type === "futures") {
          arr = await lbankFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "binance" && right.type === "spot") {
          arr = await binanceSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "binance" && right.type === "futures") {
          arr = await binanceFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "ourbit" && right.type === "futures") {
          arr = await ourbitFuturesFetchHistory({ symbol: rightSym, interval, limit });
        } else if (right.exchange === "ourbit" && right.type === "spot") {
          arr = await ourbitSpotFetchHistory({ symbol: rightSym, interval, limit });
        } else {
          throw new Error("Right side not supported");
        }
      } catch (e) {
        console.error("[right REST] error", e);
      }
      if (cancelled) return;

      for (const c of arr) upsert(rightMapRef.current, c, intervalMs, "R");
      L("right REST normalized", {
        size: rightMapRef.current.size,
        first5: Array.from(rightMapRef.current.values()).slice(0,5),
        last5:  Array.from(rightMapRef.current.values()).slice(-5),
      });
    })();

    (async () => {
      try {
        await Promise.all([leftRest, rightRest]);

        const leftHas = leftMapRef.current.size > 0;
        const rightHas = rightMapRef.current.size > 0;
        debugIntersection(leftMapRef.current, rightMapRef.current, "after REST");

        if (!leftHas || !rightHas) {
          L("one side has no data -> skip WS", { leftHas, rightHas });
          setLoading(false);
          return;
        }

        if (cancelled) return;
        recalc();

        // ------ LIVE обновления (WS или поллинг)
        const onLeft = (c) => {
          if (cancelled) return;
          upsert(leftMapRef.current, c, intervalMs, "L");
          recalc();
        };
        const onRight = (c) => {
          if (cancelled) return;
          upsert(rightMapRef.current, c, intervalMs, "R");
          recalc();
        };

        // LEFT
        if (left.exchange === "gate" && left.type === "spot") {
          leftCloseWSRef.current = gateSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "gate" && left.type === "futures") {
          leftCloseWSRef.current = gateFuturesOpenWS({
            symbol: leftSym, interval,
            onBars: (bars) => { for (const c of bars) onLeft(c); },
          });
        } else if (left.exchange === "mexc" && left.type === "spot") {
          leftCloseWSRef.current = mexcSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "mexc" && left.type === "futures") {
          leftCloseWSRef.current = mexcFuturesOpenWS({ symbol: leftSym, interval, onBar: onLeft });
        } else if (left.exchange === "bingx" && left.type === "spot") {
          leftCloseWSRef.current = bingxSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "bingx" && left.type === "futures") {
          leftCloseWSRef.current = bingxFuturesOpenWS({ symbol: leftSym, interval, onBar: onLeft });
        } else if (left.exchange === "bitget" && left.type === "spot") {
          leftCloseWSRef.current = bitgetSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "bitget" && left.type === "futures") {
          leftCloseWSRef.current = bitgetFuturesOpenWS({ symbol: leftSym, interval, onBar: onLeft });
        } else if (left.exchange === "bybit" && left.type === "spot") {
          leftCloseWSRef.current = bybitSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "bybit" && left.type === "futures") {
          leftCloseWSRef.current = bybitFuturesOpenWS({ symbol: leftSym, interval, onBar: onLeft });
        } else if (left.exchange === "kucoin" && left.type === "spot") {
          leftCloseWSRef.current = kucoinSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "kucoin" && left.type === "futures") {
          leftCloseWSRef.current = kucoinFuturesOpenWS({ symbol: leftSym, interval, onBar: onLeft });
        } else if (left.exchange === "lbank" && left.type === "spot") {
          leftCloseWSRef.current = lbankSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "lbank" && left.type === "futures") {
          // НЕТ WS — делаем REST-поллинг раз в 2 секунды (ТОЛЬКО для LBank futures)
          let lastTsL = 0;
          const tick = async () => {
            try {
              const arr = await lbankFuturesFetchHistory({ symbol: leftSym, interval, limit: 3 });
              if (!Array.isArray(arr) || arr.length === 0) return;
              const last = arr[arr.length - 1];
              if (!last?.time) return;

              if (last.time !== lastTsL && arr.length >= 2) {
                const prev = arr[arr.length - 2];
                if (prev?.time) onLeft(prev);
              }
              onLeft(last);
              lastTsL = last.time;
            } catch (e) {
              console.warn("[spread] LBank left poll error", e?.message || e);
            }
          };
          tick();
          const id = setInterval(tick, 2000);
          leftCloseWSRef.current = () => clearInterval(id);
        } else if (left.exchange === "binance" && left.type === "spot") {
          leftCloseWSRef.current = binanceSpotOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        } else if (left.exchange === "binance" && left.type === "futures") {
          leftCloseWSRef.current = binanceFuturesOpenWS({ symbol: leftSym, interval, onCandle: onLeft });
        }

        // RIGHT
        if (right.exchange === "gate" && right.type === "spot") {
          rightCloseWSRef.current = gateSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "gate" && right.type === "futures") {
          rightCloseWSRef.current = gateFuturesOpenWS({
            symbol: rightSym, interval,
            onBars: (bars) => { for (const c of bars) onRight(c); },
          });
        } else if (right.exchange === "mexc" && right.type === "spot") {
          rightCloseWSRef.current = mexcSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "mexc" && right.type === "futures") {
          rightCloseWSRef.current = mexcFuturesOpenWS({ symbol: rightSym, interval, onBar: onRight });
        } else if (right.exchange === "bingx" && right.type === "spot") {
          rightCloseWSRef.current = bingxSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "bingx" && right.type === "futures") {
          rightCloseWSRef.current = bingxFuturesOpenWS({ symbol: rightSym, interval, onBar: onRight });
        } else if (right.exchange === "bitget" && right.type === "spot") {
          rightCloseWSRef.current = bitgetSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "bitget" && right.type === "futures") {
          rightCloseWSRef.current = bitgetFuturesOpenWS({ symbol: rightSym, interval, onBar: onRight });
        } else if (right.exchange === "bybit" && right.type === "spot") {
          rightCloseWSRef.current = bybitSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "bybit" && right.type === "futures") {
          rightCloseWSRef.current = bybitFuturesOpenWS({ symbol: rightSym, interval, onBar: onRight });
        } else if (right.exchange === "kucoin" && right.type === "spot") {
          rightCloseWSRef.current = kucoinSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "kucoin" && right.type === "futures") {
          rightCloseWSRef.current = kucoinFuturesOpenWS({ symbol: rightSym, interval, onBar: onRight });
        } else if (right.exchange === "lbank" && right.type === "spot") {
          rightCloseWSRef.current = lbankSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "lbank" && right.type === "futures") {
          // НЕТ WS — делаем REST-поллинг раз в 2 секунды (ТОЛЬКО для LBank futures)
          let lastTsR = 0;
          const tick = async () => {
            try {
              const arr = await lbankFuturesFetchHistory({ symbol: rightSym, interval, limit: 3 });
              if (!Array.isArray(arr) || arr.length === 0) return;
              const last = arr[arr.length - 1];
              if (!last?.time) return;

              if (last.time !== lastTsR && arr.length >= 2) {
                const prev = arr[arr.length - 2];
                if (prev?.time) onRight(prev);
              }
              onRight(last);
              lastTsR = last.time;
            } catch (e) {
              console.warn("[spread] LBank right poll error", e?.message || e);
            }
          };
          tick();
          const id = setInterval(tick, 2000);
          rightCloseWSRef.current = () => clearInterval(id);
        } else if (right.exchange === "binance" && right.type === "spot") {
          rightCloseWSRef.current = binanceSpotOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        } else if (right.exchange === "binance" && right.type === "futures") {
          rightCloseWSRef.current = binanceFuturesOpenWS({ symbol: rightSym, interval, onCandle: onRight });
        }

        setTimeout(() => {
          // через секунду после старта live-потоков перепроверим пересечение
          debugIntersection(leftMapRef.current, rightMapRef.current, "after WS start (1s)");
        }, 1000);

      } catch (e) {
        if (!cancelled) {
          console.error("[useSpreadKlines] REST/WS error", e);
          setErr(e.message || String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try { leftCloseWSRef.current?.(); }  catch {}
      try { rightCloseWSRef.current?.(); } catch {}
      leftCloseWSRef.current  = null;
      rightCloseWSRef.current = null;

      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [key]);

  return { candles, loading, error: err };
}

// публичные экспорт(ы)
export function useSpreadKlines(params) {
  return _useSpreadKlinesImpl(params);
}
export default useSpreadKlines;
